import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Meta from 'src/components/Meta';
import { Divider, PageCard, PageChartSkeleton, PageContainer, Spacer } from 'src/common';
import ScenariosCompare from './comps/scenarios/ScenariosCompare';
import { useGaEventPageView } from 'src/utils/gaEvent';
import { Charts } from './comps/charts';
import { AssessmentVersion } from 'src/api/types';
import useVersions from '../useVersions';
import { isCurrentVersionId, versionCreatedAtTime } from 'src/pages/compare/orderVersions';
import styled from 'styled-components';
import { useComparisonAssessmentQuery } from 'src/graphql/_generated';

const Card = styled(PageCard)`
  padding: 0;
  padding: 20px 0px 30px 0px;

  .paddings {
    padding: 0px 30px 0px 30px;
  }
`;

const CompareAssessment = () => {
  const { idA, idB } = useParams<{ idA: string; idB: string }>();
  const { versionAId, versionBId } = useVersions();
  const [versionA, setVersionA] = useState<AssessmentVersion | undefined>(undefined);
  const [versionB, setVersionB] = useState<AssessmentVersion | undefined>(undefined);
  const [tab, setTab] = useState<0 | 1 | 2>(0);

  // When comparing two versions of the SAME assessment, idA === idB, so the aliased
  // assessmentA/assessmentB results normalise to one Apollo cache entity (Assessment:<id>)
  // and clobber each other. 'no-cache' reads straight from the network response so the two
  // versions stay distinct. nextFetchPolicy must also be 'no-cache' to override the global
  // 'cache-first' default, otherwise the collision returns after the first fetch.
  const { data: compareData, loading: isLoading } = useComparisonAssessmentQuery({
    variables: {
      assessmentAId: idA!,
      assessmentBId: idB!,
      versionAId,
      versionBId,
    },
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (compareData?.assessmentA?.versions) {
      const version = compareData.assessmentA.versions.find((v) => v.id === versionAId);
      setVersionA(version);
    }
    if (compareData?.assessmentB?.versions) {
      const version = compareData.assessmentB.versions.find((v) => v.id === versionBId);
      setVersionB(version);
    }
  }, [versionAId, versionBId, compareData]);

  useGaEventPageView('Cydea | Compare Assessments');

  if (isLoading || !compareData?.assessmentA || !compareData?.assessmentB)
    return (
      <PageContainer>
        <PageCard>
          <PageChartSkeleton />
        </PageCard>
      </PageContainer>
    );

  // Always present the most recent version as "Version 1" / series A across the whole comparison
  // view (labels, legend, charts, tabs and tables), regardless of the order vaId/vbId arrived in the
  // URL. The live/[CURRENT] state (no version id) is treated as the newest. Only same-assessment
  // version comparisons are reordered; different-assessment comparisons keep the requested order.
  const isSameAssessment = idA === idB;
  const rankOf = (versionId?: string, version?: AssessmentVersion) =>
    isCurrentVersionId(versionId) ? Number.POSITIVE_INFINITY : versionCreatedAtTime(version?.meta?.createdAt);
  const swap = isSameAssessment && rankOf(versionBId, versionB) > rankOf(versionAId, versionA);

  const dataA = swap ? compareData.assessmentB : compareData.assessmentA;
  const dataB = swap ? compareData.assessmentA : compareData.assessmentB;
  const orderedVersionA = swap ? versionB : versionA;
  const orderedVersionB = swap ? versionA : versionB;

  const showProjectedA = !!dataA.scenarios
    ?.flatMap((scenario) =>
      scenario.riskManagementPlans
        ?.filter((plan) => plan.control && plan.control.status !== 'Abandoned' && plan.control.status !== 'Retired')
        ?.flatMap((plan) => plan.modifiers),
    )
    ?.filter((modifier) => modifier.type !== 'NOTE_TYPE')?.length;

  const showProjectedB = !!dataB.scenarios
    ?.flatMap((scenario) =>
      scenario.riskManagementPlans
        ?.filter((plan) => plan.control && plan.control.status !== 'Abandoned' && plan.control.status !== 'Retired')
        ?.flatMap((plan) => plan.modifiers),
    )
    ?.filter((modifier) => modifier.type !== 'NOTE_TYPE')?.length;

  return (
    <>
      <Meta title={`Cydea | Compare Assessments`} feedbackScript />
      <PageContainer data-cy="compare-assessments-page">
        <Card>
          <Charts
            dataA={dataA}
            dataB={dataB}
            versionA={orderedVersionA}
            versionB={orderedVersionB}
            assessmentA={{
              seriesEnabled: tab === 0 || tab === 1,
              showProjected: showProjectedA,
            }}
            assessmentB={{
              seriesEnabled: tab === 0 || tab === 2,
              showProjected: showProjectedB,
            }}
          />

          <Spacer $px={20} />
          <Divider />
          <Spacer $px={30} />
          <div className="paddings">
            <ScenariosCompare
              tab={tab}
              setTab={setTab}
              dataA={dataA}
              dataB={dataB}
              versionA={orderedVersionA}
              versionB={orderedVersionB}
            />
          </div>
        </Card>
      </PageContainer>
    </>
  );
};

export default CompareAssessment;
